/**
 * The chart's colours, resolved from the CSS custom properties in `index.css`.
 *
 * A canvas cannot take a `var()`, and hardcoding hex in components would
 * recreate the second palette this file exists to remove. So the tokens are
 * read at mount and converted.
 *
 * They also have to be converted, and that survives the v5 upgrade.
 * lightweight-charts 5.1.0 parses colours by handing them to
 * `getComputedStyle` and matching `rgb()`/`rgba()` on the way back. Per CSS
 * Color 4 a non-legacy colour function round-trips in its own space, so
 * `oklch(...)` comes back as `oklch(...)` and never matches. v4.2.3 threw
 * `Cannot parse color`; v5 throws `Failed to parse color`. The library's own
 * typings name Oklch as a case needing a custom parser, which is what
 * `CHART_COLOR_PARSERS` is.
 *
 * NEVER call `readPalette` at module scope. Call it inside a hook, memoised
 * once per mount, so a series and its legend chip resolve the same string.
 */

/** Fallbacks per token. Also the values every chart test sees, since jsdom
 *  cannot run the round-trip. Kept close to `index.css` by eye; they are a
 *  degraded mode, not a second source of truth. */
const FALLBACK = {
  "--color-text": "#f0f0f2",
  "--color-text-muted": "#a4a6ad",
  "--color-border": "#3f4249",
  "--color-up": "#22c55e",
  "--color-down": "#e5484d",
  "--color-accent": "#2a9dd6",
  "--color-danger": "#e5484d",
  "--color-warning": "#e0a92b",
  "--color-surface": "#191b1f",
} as const;

type TokenName = keyof typeof FALLBACK;

export interface ChartPalette {
  text: string;
  muted: string;
  border: string;
  grid: string;
  up: string;
  down: string;
  warning: string;
  surface: string;
}

/** Hex, `rgb()`, `rgba()`, `hsl()`, `hwb()`, and named colours are v5's own
 *  business. Everything else — `oklch()`, `lab()`, `color()` — is ours. */
function needsConversion(value: string): boolean {
  return /^(oklch|oklab|lab|lch|color)\s*\(/i.test(value.trim());
}

/**
 * Any CSS colour the browser understands, as the `[r, g, b, a]` tuple
 * lightweight-charts wants: paint one pixel and read it back.
 *
 * Returns null when there is no canvas to paint on, which is jsdom, and when
 * the value is one the library already handles — v5 should keep its own path
 * for those rather than round-tripping every colour through a canvas.
 */
function paintToRgba(value: string): [number, number, number, number] | null {
  if (!needsConversion(value)) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = value;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    if (r === undefined || g === undefined || b === undefined || a === undefined) return null;
    return [r, g, b, a / 255];
  } catch {
    return null;
  }
}

export const CHART_COLOR_PARSERS: readonly ((
  color: string,
) => [number, number, number, number] | null)[] = [paintToRgba];

function token(name: TokenName): string {
  const fallback = FALLBACK[name];
  if (typeof getComputedStyle !== "function") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (raw === "") return fallback;
  const rgba = paintToRgba(raw);
  if (rgba) return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3].toFixed(3)})`;
  return needsConversion(raw) ? fallback : raw;
}

/**
 * Alpha over an already-converted colour.
 *
 * Throws rather than passing the input through. The reference returns its
 * argument unchanged when it does not match `^#rrggbb$`, which drops the alpha
 * without a word and surfaces as a parse error one frame later in a different
 * file — or, in its `toRgba`, as a hardcoded green where a red candle should be.
 */
export function withAlpha(color: string, alpha: number): string {
  const parts = /^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(color);
  if (parts) return `rgba(${parts[1]}, ${parts[2]}, ${parts[3]}, ${alpha})`;
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (hex) {
    const [r, g, b] = [hex[1], hex[2], hex[3]].map((h) => Number.parseInt(h as string, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  throw new Error(`withAlpha: cannot parse ${color}. Resolve it through readPalette first.`);
}

/** `withAlpha` for a value that is already known good — the fallbacks are hex
 *  and every converted token is `rgba()`, so this cannot throw in practice. */
function withAlphaSafe(color: string, alpha: number): string {
  try {
    return withAlpha(color, alpha);
  } catch {
    return color;
  }
}

export function readPalette(): ChartPalette {
  const border = token("--color-border");
  return {
    text: token("--color-text"),
    muted: token("--color-text-muted"),
    border,
    grid: withAlphaSafe(border, 0.55),
    up: token("--color-up"),
    down: token("--color-down"),
    warning: token("--color-warning"),
    surface: token("--color-surface"),
  };
}

/**
 * The mono stack, resolved for a canvas, which cannot take a `var()`.
 *
 * Reads `--stack-mono` rather than `--font-data`. Tailwind emits a theme
 * variable onto `:root` only while some utility still references it, so
 * `--font-data` disappears the moment the last `font-data` class does — and
 * because `@theme inline` has already inlined the stack into the utilities
 * that survive, nothing would look broken while this returned an empty
 * string. `--stack-mono` is the plain `:root` declaration it aliases, and a
 * plain declaration cannot be tree-shaken away.
 */
export function readFontStack(): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue("--stack-mono").trim();
  return value || "ui-monospace, SFMono-Regular, Menlo, monospace";
}
